import type Hls from 'hls.js';
import { state, markCam } from '@/store/state';
import { lowUrl } from '@/config/streams';
import { isYoonee, cctvStreamUrl } from '@/config/cctv';
import { ensureHls, canPlayNativeHls, gridConfig } from './hls';
import { makeVideo, playSafe } from './video';
import { pool } from './pool';

/**
 * Re-tests wall cameras that the "online only" filter is hiding.
 *
 * That filter is otherwise self-sealing: a hidden camera has no tile, so nothing
 * ever retries it, so it can never be seen to recover. This probes them off to
 * the side instead — one at a time, on a per-camera backoff — and marks one that
 * plays back 'online', which puts it on the wall again without the user touching
 * the filter.
 *
 * The probe is a detached <video> rather than a fetch, deliberately: it exercises
 * the real playback path, so it is accurate for both providers and sidesteps the
 * CCTV media origin's missing CORS headers, which would fail a fetch that a
 * native media load handles fine.
 */

const HEARTBEAT = 15_000;   // how often to look for a camera that is due
const PROBE_TIMEOUT = 9_000;
const BACKOFF = [45_000, 90_000, 180_000, 360_000, 600_000] as const;

interface Attempt { next: number; fails: number; }
const attempts = new Map<string, Attempt>();
let probing = false;

/** Cameras on the wall that the filter is currently hiding. */
function hiddenOnWall(): string[] {
  if (!state.view.onlineOnly) return [];
  const out: string[] = [];
  for (const id of state.onWall) {
    if (!id) continue;
    const c = state.cameras[id];
    if (c && (c.status === 'offline' || c.status === 'absent')) out.push(id);
  }
  return out;
}

/** Can this URL actually start playing? Resolves false rather than throwing. */
async function canPlay(url: string, ms: number): Promise<boolean> {
  const v = makeVideo();
  v.preload = 'auto';
  v.style.cssText = 'position:absolute;left:-9999px;top:0;width:1px;height:1px;opacity:0;pointer-events:none';
  document.body.appendChild(v);
  let hls: Hls | null = null;
  try {
    if (canPlayNativeHls(v)) {
      v.src = url;
    } else {
      const Ctor = await ensureHls();
      if (!Ctor.isSupported()) return false;
      hls = new Ctor(gridConfig());
      hls.loadSource(url);
      hls.attachMedia(v);
    }
    playSafe(v);
    return await new Promise<boolean>((resolve) => {
      let done = false;
      const finish = (ok: boolean): void => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        v.removeEventListener('playing', onPlaying);
        resolve(ok);
      };
      const onPlaying = (): void => finish(true);
      const timer = window.setTimeout(() => finish(false), ms);
      v.addEventListener('playing', onPlaying);
    });
  } catch {
    return false;
  } finally {
    try { hls?.destroy(); } catch { /* noop */ }
    try { v.pause(); v.removeAttribute('src'); v.load(); } catch { /* noop */ }
    v.remove();
  }
}

async function probeOne(id: string): Promise<void> {
  const cam = state.cameras[id];
  if (!cam) return;
  let url: string;
  try {
    url = isYoonee(cam) ? await cctvStreamUrl(id) : lowUrl(id);
  } catch {
    return bump(id); // no session / no token — treat as still down
  }
  // the camera may have been removed or the filter turned off while awaiting
  if (!state.cameras[id] || !state.view.onlineOnly) return;

  if (await canPlay(url, PROBE_TIMEOUT)) {
    attempts.delete(id);
    // 'online' (reachable), not 'live' — the tile sets 'live' once it decodes.
    if (state.cameras[id]) markCam(state.cameras[id]!, 'online');
  } else {
    bump(id);
  }
}

function bump(id: string): void {
  const a = attempts.get(id) ?? { next: 0, fails: 0 };
  const wait = BACKOFF[Math.min(a.fails, BACKOFF.length - 1)]!;
  a.fails += 1;
  a.next = Date.now() + wait;
  attempts.set(id, a);
}

async function sweep(): Promise<void> {
  if (probing || document.hidden || pool.paused) return;
  const now = Date.now();
  const due = hiddenOnWall().filter((id) => (attempts.get(id)?.next ?? 0) <= now);
  if (!due.length) return;
  // oldest-tried first, so one stubborn camera cannot starve the rest
  due.sort((a, b) => (attempts.get(a)?.next ?? 0) - (attempts.get(b)?.next ?? 0));
  probing = true;
  try { await probeOne(due[0]!); } finally { probing = false; }
}

/** Start the background revive loop. Safe to call once, at boot. */
export function startReviver(): void {
  window.setInterval(() => void sweep(), HEARTBEAT);
}

/** Forget backoffs, so hidden cameras are all retried promptly. */
export function resetReviver(): void {
  attempts.clear();
}
